/*
 * Art wall configurator (sections/main-art-wall.liquid)
 * Vanilla JS, no dependencies. One instance per [data-art-wall] root.
 *
 * Frame rendering: each card keeps the plain poster image and the frame state
 * separate (frame = colour key 'oak' | 'black' | 'dark-oak' | 'white' | 'none').
 * If window.PeletonFrames (feature/frame-compositing) is present, the card asks it
 * to render; otherwise the built-in CSS frame is used. A 'peleton:frame-change'
 * event is dispatched on every frame change.
 */
(() => {
  if (window.PeletonArtWall) return;

  const A_SIZES = {
    a6: [10.5, 14.8], a5: [14.8, 21], a4: [21, 29.7], a3: [29.7, 42],
    a2: [42, 59.4], a1: [59.4, 84.1], a0: [84.1, 118.9]
  };

  const norm = (s) => String(s || '').toLowerCase().replace(/×/g, 'x').replace(/cm/g, '').replace(/\s+/g, '');

  const dims = (title) => {
    const n = norm(title);
    if (A_SIZES[n]) return A_SIZES[n].slice();
    const m = n.match(/(\d+(?:[.,]\d+)?)x(\d+(?:[.,]\d+)?)/);
    if (m) return [parseFloat(m[1].replace(',', '.')), parseFloat(m[2].replace(',', '.'))];
    return null;
  };

  // Inch countries: the label comes from snippets/size-label.liquid (window.peletonSizeLabels).
  const imperialLabel = (title, style) => {
    const m = window.peletonSizeLabels;
    const key = String(title == null ? '' : title).trim();
    return m && m.units === 'in' && m[style] && Object.prototype.hasOwnProperty.call(m[style], key) ? m[style][key] : null;
  };

  const sizeLabel = (title) => {
    const imp = imperialLabel(title, 'long');
    if (imp) return imp;
    const n = norm(title);
    if (A_SIZES[n]) {
      const [w, h] = A_SIZES[n];
      return `${title} (${Math.round(w)} x ${Math.round(h)} cm)`;
    }
    const d = dims(title);
    return d ? `${d[0]} x ${d[1]} cm` : title;
  };

  const shortSize = (title) => {
    const imp = imperialLabel(title, 'short');
    if (imp) return imp;
    const d = dims(title);
    if (!d) return title;
    return A_SIZES[norm(title)] ? String(title).toUpperCase() : `${d[0]}x${d[1]} cm`;
  };

  // Colour key from a frame/passe-partout product handle + title (universal heuristics).
  const colourKey = (p) => {
    const s = `${p.handle} ${p.title}`.toLowerCase();
    if (/sort|black|noir/.test(s)) return 'black';
    if (/m(ø|o|oe)rk|dark/.test(s)) return 'dark-oak';
    if (/hvid|white|blanc/.test(s)) return 'white';
    if (/ege|oak|eg\b|natur/.test(s)) return 'oak';
    if (/valn|walnut/.test(s)) return 'dark-oak';
    return p.handle;
  };

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const parseMap = (txt) => {
    const map = {};
    String(txt || '').split(/[\n,;]+/).forEach((line) => {
      const [a, b] = line.split(/=|:|->|→/);
      if (a && b) map[norm(a)] = norm(b);
    });
    return map;
  };

  const pick = (arr, i) => {
    if (!Array.isArray(arr) || !arr.length) return '';
    const v = arr.length === 1 ? arr[0] : arr[i];
    return String(v == null ? '' : v).trim();
  };

  const CHEVRON = '<svg class="aw-select__chev" aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4.25 6 8l4-3.75" stroke="currentColor" stroke-width="1.3"/></svg>';
  const CHECK = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="m2.5 7.2 3 3 6-6.4" stroke="currentColor" stroke-width="1.5"/></svg>';
  // Same bag as the header cart icon (snippets/icon-cart.liquid, 'bag').
  const BAG = '<svg class="aw-add__icon" aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.7 7c-1 0-1.8.7-2 1.7L3 20c0 1.1 2 2 9 2s9-.9 9-2L19.3 8.7c-.2-1-1-1.7-2-1.7H6.7z"/><path d="M8 7c0-2.2.5-5 4-5s4 2.8 4 5"/></svg>';

  class ArtWall {
    constructor(root) {
      this.root = root;
      const raw = root.querySelector('[data-aw-data]');
      try { this.data = JSON.parse(raw.textContent); } catch (e) { console.error('[art-wall] bad data', e); return; }
      this.t = this.data.t || {};
      this.matMap = parseMap(this.data.matMap);
      this.frames = (this.data.frames || []).filter(Boolean).map((f) => ({ ...f, key: colourKey(f) }));
      this.mats = (this.data.mats || []).filter(Boolean).map((m) => ({ ...m, key: colourKey(m) }));
      this.els = {
        track: root.querySelector('[data-aw-track]'),
        summary: root.querySelector('[data-aw-summary]'),
        counts: root.querySelector('[data-aw-counts]'),
        plan: root.querySelector('[data-aw-plan]'),
        total: root.querySelector('[data-aw-total]'),
        compare: root.querySelector('[data-aw-compare]'),
        save: root.querySelector('[data-aw-save]'),
        cta: root.querySelector('[data-aw-cta]'),
        ctaLabel: root.querySelector('[data-aw-cta-label]'),
        msg: root.querySelector('[data-aw-msg]'),
        pagerCount: root.querySelector('[data-aw-pager-count]'),
        pagerDots: root.querySelector('[data-aw-pager-dots]'),
        sheet: root.querySelector('[data-aw-sheet]'),
        sheetTitle: root.querySelector('[data-aw-sheet-title]'),
        options: root.querySelector('[data-aw-options]'),
        gallery: root.querySelector('[data-aw-gallery]'),
        galleryDots: root.querySelector('[data-aw-gallery-dots]')
      };
      this.items = (this.data.posters || []).map((p, i) => this.initItem(p, i)).filter(Boolean);
      this.active = 0;
      this.build();
      this.bind();
      this.refresh();
    }

    /* ---------- model ---------- */

    initItem(p, i) {
      const variants = (p.variants || []).filter(Boolean);
      if (!variants.length) return null;
      const d = this.data.defaults || {};
      // Per-poster setup (Wall items metaobject) wins over the positional defaults.
      const w = p.want || {};
      const wantSize = norm(w.size || pick(d.sizes, i));
      let v = variants.find((x) => x.available && wantSize && norm(x.title) === wantSize)
        || variants.find((x) => x.available)
        || variants[0];
      const item = { p, i, variants, variantId: v.id, frame: 'none', mat: 'none', removed: !v.available };

      const wantFrame = (w.frame || pick(d.frames, i) || d.frame || 'none').toLowerCase();
      const frame = wantFrame === 'none' ? null : this.frames.find((f) => f.key === wantFrame || f.handle === wantFrame);
      if (frame) item.frame = frame.key;

      const wantMat = (w.mat || pick(d.mats, i) || 'none').toLowerCase();
      const mat = wantMat === 'none' ? null : this.mats.find((m) => m.key === wantMat || m.handle === wantMat);
      if (mat) item.mat = mat.key;

      this.validate(item);
      item.def = { variantId: item.variantId, frame: item.frame, mat: item.mat };
      return item;
    }

    variant(item) { return item.variants.find((v) => v.id === item.variantId) || item.variants[0]; }
    frameBy(key) { return this.frames.find((f) => f.key === key); }
    matBy(key) { return this.mats.find((m) => m.key === key); }

    findVar(product, sizeN) {
      if (!product || !sizeN) return null;
      return (product.variants || []).find((v) => norm(v.title) === sizeN) || null;
    }

    // Frame size the item needs (one size up with passe-partout).
    frameSizeN(item, matKey = item.mat) {
      const sizeN = norm(this.variant(item).title);
      if (matKey !== 'none') return this.matMap[sizeN] || null;
      return sizeN;
    }

    // Keep the item in a purchasable state after any change.
    validate(item) {
      const sizeN = norm(this.variant(item).title);
      if (item.mat !== 'none') {
        const matN = this.matMap[sizeN];
        const mv = this.findVar(this.matBy(item.mat), matN);
        const fv = this.findVar(this.frameBy(item.frame), matN);
        if (!matN || !mv || !mv.available || !fv || !fv.available) item.mat = 'none';
      }
      if (item.frame !== 'none') {
        const fv = this.findVar(this.frameBy(item.frame), this.frameSizeN(item));
        if (!fv || !fv.available) { item.frame = 'none'; item.mat = 'none'; }
      }
    }

    lines(item) {
      const v = this.variant(item);
      const out = [{ kind: 'poster', title: item.p.title, sub: shortSize(v.title), v }];
      if (item.frame !== 'none') {
        const f = this.frameBy(item.frame);
        const fv = this.findVar(f, this.frameSizeN(item));
        if (fv) out.push({ kind: 'frame', title: f.title, sub: shortSize(fv.title), v: fv });
      }
      if (item.mat !== 'none') {
        const m = this.matBy(item.mat);
        const mv = this.findVar(m, this.frameSizeN(item));
        if (mv) out.push({ kind: 'mat', title: m.title, sub: shortSize(mv.title), v: mv });
      }
      return out;
    }

    sums(lines) {
      return lines.reduce((acc, l) => {
        acc.price += l.v.price;
        acc.compare += Math.max(l.v.compare || 0, l.v.price);
        return acc;
      }, { price: 0, compare: 0 });
    }

    money(cents) {
      const fmt = this.data.moneyFormat;
      try {
        if (window.Woolman && Woolman.Utils && Woolman.Utils.formatMoney) return Woolman.Utils.formatMoney(cents, fmt);
      } catch (e) { /* fall through */ }
      const value = (cents / 100).toFixed(2).replace(/\.00$/, '').replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return String(fmt || '{{amount}} kr').replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    priceHtml(price, compare) {
      if (compare && compare > price) {
        return `<span class="aw-price"><span class="aw-price__now is-sale">${this.money(price)}</span><s class="aw-price__was">${this.money(compare)}</s></span>`;
      }
      return `<span class="aw-price"><span class="aw-price__now">${this.money(price)}</span></span>`;
    }

    changed(item) {
      return item.variantId !== item.def.variantId || item.frame !== item.def.frame || item.mat !== item.def.mat;
    }

    /* ---------- options ---------- */

    options(item, kind) {
      const t = this.t;
      const sizeN = norm(this.variant(item).title);
      if (kind === 'size') {
        return item.variants.map((v) => ({
          value: v.id, label: sizeLabel(v.title), price: v.price, compare: v.compare,
          disabled: !v.available, note: v.available ? '' : t.soldOut, selected: v.id === item.variantId
        }));
      }
      if (kind === 'frame') {
        const fsN = this.frameSizeN(item);
        const opts = [{ value: 'none', label: t.noFrame, selected: item.frame === 'none', swatch: 'none' }];
        this.frames.forEach((f) => {
          const fv = this.findVar(f, fsN);
          if (!fv) return;
          opts.push({
            value: f.key, label: f.title, sub: shortSize(fv.title), price: fv.price, compare: fv.compare,
            disabled: !fv.available, note: fv.available ? '' : t.soldOut, selected: item.frame === f.key, swatch: f.key
          });
        });
        return opts;
      }
      if (kind === 'mat') {
        const matN = this.matMap[sizeN];
        const opts = [{ value: 'none', label: t.noMat, selected: item.mat === 'none', swatch: 'none' }];
        this.mats.forEach((m) => {
          const mv = this.findVar(m, matN);
          if (!mv) return;
          // A passe-partout needs the chosen frame (or any frame) in the larger size.
          const frameKey = item.frame !== 'none' ? item.frame : null;
          const fv = frameKey ? this.findVar(this.frameBy(frameKey), matN) : null;
          const ok = mv.available && (!frameKey || (fv && fv.available));
          opts.push({
            value: m.key, label: m.title, sub: (t.frameSize || '').replace('[size]', shortSize(mv.title)),
            price: mv.price, compare: mv.compare, disabled: !ok,
            note: !mv.available ? t.soldOut : (!ok ? t.noFramesInSize : ''), selected: item.mat === m.key, swatch: `mat-${m.key}`
          });
        });
        return opts;
      }
      return [];
    }

    hasMatOptions(item) {
      if (!this.mats.length) return false;
      return this.options(item, 'mat').length > 1;
    }

    select(item, kind, value) {
      const before = item.frame;
      if (kind === 'size') {
        item.variantId = Number(value);
      } else if (kind === 'frame') {
        item.frame = value;
        if (value === 'none') item.mat = 'none';
      } else if (kind === 'mat') {
        item.mat = value;
        if (value !== 'none' && item.frame === 'none') {
          // Choosing a passe-partout without a frame: use the wall's default frame (or the first that fits).
          const matN = this.matMap[norm(this.variant(item).title)];
          const pref = this.frameBy(item.def.frame) || this.frameBy((this.data.defaults || {}).frame);
          const ok = (f) => { const fv = this.findVar(f, matN); return fv && fv.available; };
          const f = (pref && ok(pref)) ? pref : this.frames.find(ok);
          if (f) item.frame = f.key;
        }
      }
      this.validate(item);
      if (item.frame !== before) {
        document.dispatchEvent(new CustomEvent('peleton:frame-change', { detail: { frame: item.frame, index: item.i, source: 'art-wall' } }));
      }
      this.updateCard(item);
      this.refresh();
    }

    /* ---------- render ---------- */

    build() {
      const { track } = this.els;
      if (!track) return;
      track.innerHTML = '';
      this.items.forEach((item, n) => {
        const el = document.createElement('article');
        el.className = 'aw-card';
        el.dataset.index = n;
        el.setAttribute('role', 'group');
        el.setAttribute('aria-roledescription', 'slide');
        el.innerHTML = `
          <a class="aw-card__stage" href="${esc(item.p.url)}" tabindex="-1" aria-hidden="true">
            <span class="aw-art" data-aw-art>
              <span class="aw-art__frame">
                <span class="aw-art__mat">
                  <img class="aw-art__img" src="${esc(item.p.image)}" srcset="${esc(item.p.image)} 640w, ${esc(item.p.image2x)} 1100w" sizes="(min-width: 990px) 18vw, 80vw" alt="" loading="${n < 3 ? 'eager' : 'lazy'}" decoding="async">
                </span>
              </span>
            </span>
          </a>
          <div class="aw-card__body">
            <p class="aw-card__eyebrow">${esc(this.t.poster)}${item.p.vendor ? ` · ${esc(item.p.vendor)}` : ''}</p>
            <h3 class="aw-card__title"><a href="${esc(item.p.url)}">${esc(item.p.title)}</a></h3>
            <div class="aw-card__controls" data-aw-controls></div>
            <div class="aw-card__foot" data-aw-foot></div>
          </div>`;
        track.appendChild(el);
        item.el = el;
        const img = el.querySelector('.aw-art__img');
        const loaded = () => img.classList.add('is-loaded');
        if (img.complete && img.naturalWidth) loaded(); else { img.addEventListener('load', loaded, { once: true }); img.addEventListener('error', loaded, { once: true }); }
        this.updateCard(item);
      });
      if (this.els.pagerDots) {
        this.els.pagerDots.innerHTML = this.items.map((_, n) => `<span${n === 0 ? ' class="is-active"' : ''}></span>`).join('');
      }
    }

    trigger(item, kind, label, valueHtml, sub, opts = {}) {
      const id = `aw-${this.data.wall.handle}-${item.i}-${kind}`;
      return `
        <button type="button" class="aw-select${opts.placeholder ? ' is-placeholder' : ''}" id="${esc(id)}" data-aw-open="${kind}"
          aria-haspopup="listbox" aria-expanded="false"${opts.disabled ? ' disabled aria-disabled="true"' : ''}>
          <span class="aw-select__kicker">${esc(label)}</span>
          <span class="aw-select__value">
            <span class="aw-select__main">${valueHtml}</span>
            ${sub ? `<span class="aw-select__sub">${esc(sub)}</span>` : ''}
          </span>
          ${opts.price || ''}
          ${CHEVRON}
        </button>`;
    }

    updateCard(item) {
      const el = item.el;
      if (!el) return;
      const t = this.t;
      const v = this.variant(item);
      const lines = this.lines(item);
      const frameLine = lines.find((l) => l.kind === 'frame');
      const matLine = lines.find((l) => l.kind === 'mat');

      el.classList.toggle('is-removed', item.removed);
      el.setAttribute('aria-label', `${item.p.title}${item.removed ? ` – ${t.removed}` : ''}`);

      // Art
      this.paintArt(el.querySelector('[data-aw-art]'), item);

      // Controls
      const frameOpts = this.options(item, 'frame');
      const noFrames = frameOpts.length <= 1;
      let html = this.trigger(item, 'size', t.size, esc(sizeLabel(v.title)), '', { price: this.priceHtml(v.price, v.compare) });
      html += this.trigger(
        item, 'frame', t.frame,
        frameLine ? esc(frameLine.title) : esc(noFrames ? t.noFramesInSize : t.noFrame),
        frameLine ? frameLine.sub : '',
        { price: frameLine ? this.priceHtml(frameLine.v.price, frameLine.v.compare) : '', disabled: noFrames, placeholder: !frameLine }
      );
      if (this.hasMatOptions(item)) {
        html += this.trigger(
          item, 'mat', t.mat,
          matLine ? esc(matLine.title) : esc(t.chooseMat),
          matLine ? (t.frameSize || '').replace('[size]', matLine.sub) : '',
          { price: matLine ? this.priceHtml(matLine.v.price, matLine.v.compare) : '', placeholder: !matLine }
        );
      }
      el.querySelector('[data-aw-controls]').innerHTML = html;

      // Foot: per-card "add this poster" button (Desenio pattern: card price | bag), then Nulstil / Fjern.
      const s = this.sums(lines);
      const state = item.addState || '';
      const hadFocus = !!(document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-aw-add]') && el.contains(document.activeElement));
      const addLabel = state === 'done' ? (t.addedOne || t.addOne) : t.addOne;
      el.querySelector('[data-aw-foot]').innerHTML = `
        ${item.removed ? `<p class="aw-card__removed">${esc(t.removed)}</p>` : `
        <button type="button" class="aw-add${state ? ` is-${state}` : ''}" data-aw-add
          aria-label="${esc(`${t.addOne} – ${item.p.title}, ${this.money(s.price)}`)}"${state === 'loading' ? ' aria-busy="true"' : ''}>
          <span class="aw-add__price">${this.priceHtml(s.price, s.compare)}</span>
          <span class="aw-add__sep" aria-hidden="true"></span>
          <span class="aw-add__cta">${state === 'done' ? CHECK : BAG}<span class="aw-add__label">${esc(addLabel)}</span></span>
        </button>`}
        <span class="aw-card__actions">
          ${this.changed(item) && !item.removed ? `<button type="button" class="aw-link" data-aw-reset>${esc(t.reset)}</button>` : ''}
          <button type="button" class="aw-link" data-aw-remove aria-pressed="${item.removed}">${esc(item.removed ? t.restore : t.remove)}</button>
        </span>
        <p class="aw-card__msg" data-aw-card-msg role="status" aria-live="polite">${item.addError ? esc(item.addError) : ''}</p>`;
      el.querySelectorAll('[data-aw-open]').forEach((b) => { if (item.removed) b.disabled = true; });
      if (hadFocus) { const again = el.querySelector('[data-aw-add]'); if (again) again.focus({ preventScroll: true }); }
    }

    paintArt(art, item) {
      if (!art) return;
      const v = this.variant(item);
      const landscape = (item.p.ratio || 0.7) > 1.02;
      const orient = (d) => (d ? (landscape ? [Math.max(d[0], d[1]), Math.min(d[0], d[1])] : [Math.min(d[0], d[1]), Math.max(d[0], d[1])]) : null);
      const pd = orient(dims(v.title)) || (landscape ? [70, 50] : [50, 70]);
      let outer = pd;
      let pad = [0, 0];
      if (item.mat !== 'none') {
        const fd = orient(dims(this.frameSizeN(item)));
        if (fd) {
          outer = fd;
          pad = [((fd[0] - pd[0]) / 2) / fd[0] * 100, ((fd[1] - pd[1]) / 2) / fd[0] * 100];
        }
      }
      // Never crop the artwork: without a mat the frame hugs the image's own ratio
      // (with a mat, the image is contained inside the mat opening).
      if (item.mat === 'none' && item.p.ratio > 0) outer = [item.p.ratio * 100, 100];
      const ar = outer[0] / outer[1];
      // Fit inside the stage (4:5) with breathing room.
      const STAGE = 0.8;
      const MAX = 72;
      const width = ar < STAGE ? MAX * ar / STAGE : MAX;
      art.style.setProperty('--aw-ar', `${outer[0]} / ${outer[1]}`);
      art.style.setProperty('--aw-w', `${width.toFixed(2)}%`);
      art.style.setProperty('--aw-pad-x', `${pad[0].toFixed(3)}%`);
      art.style.setProperty('--aw-pad-y', `${pad[1].toFixed(3)}%`);
      art.dataset.frame = item.frame;
      art.dataset.mat = item.mat;
      // Frame compositing: draw the real moulding (the frame's overlay texture, see
      // snippets/framed-poster-config.liquid) over our own frame box, in place of the
      // flat colour. Falls back to the CSS frame when compositing is off or the key unknown.
      const P = window.PeletonFrames;
      const box = art.querySelector('.aw-art__frame');
      const composite = !!(P && typeof P.get === 'function' && item.frame !== 'none' && P.get(item.frame) && P.get(item.frame).overlay && box);
      art.classList.toggle('is-composited', composite);
      if (composite) {
        art.setAttribute('data-pf-frame', item.frame);
        if (!box.querySelector(':scope > .pf__frame')) {
          const moulding = document.createElement('span');
          moulding.className = 'pf__frame';
          moulding.setAttribute('aria-hidden', 'true');
          box.appendChild(moulding);
        }
      } else {
        art.removeAttribute('data-pf-frame');
      }
    }

    renderSummary() {
      const { summary } = this.els;
      if (!summary) return;
      const t = this.t;
      summary.innerHTML = this.items.map((item, n) => {
        const lines = this.lines(item);
        const s = this.sums(lines);
        const kindLabel = { poster: t.poster, frame: t.frame, mat: t.mat };
        return `
          <li class="aw-sum${item.removed ? ' is-removed' : ''}" data-index="${n}">
            <span class="aw-sum__thumb"><span class="aw-art aw-art--mini" data-aw-mini><span class="aw-art__frame"><span class="aw-art__mat"><img class="aw-art__img" src="${esc(item.p.thumb)}" alt="" loading="lazy"></span></span></span></span>
            <div class="aw-sum__body">
              <p class="aw-sum__head">
                <a class="aw-sum__title" href="${esc(item.p.url)}">${esc(item.p.title)}</a>
                ${item.removed ? '' : `<span class="aw-sum__total">${this.priceHtml(s.price, s.compare)}</span>`}
              </p>
              ${item.removed ? `<p class="aw-sum__removed">${esc(t.removed)}</p>` : `
              <ul class="aw-sum__lines">
                ${lines.map((l) => `
                  <li class="aw-sum__line">
                    <span class="aw-sum__kind">${esc(kindLabel[l.kind])}</span>
                    <span class="aw-sum__what">${l.kind === 'poster' ? '' : `${esc(l.title)}, `}${esc(l.sub)}</span>
                    ${this.priceHtml(l.v.price, l.v.compare)}
                  </li>`).join('')}
              </ul>`}
              <button type="button" class="aw-link" data-aw-remove aria-pressed="${item.removed}">${esc(item.removed ? t.restore : t.remove)}</button>
            </div>
          </li>`;
      }).join('');
      summary.querySelectorAll('[data-aw-mini]').forEach((art) => {
        const item = this.items[Number(art.closest('[data-index]').dataset.index)];
        this.paintArt(art, item);
      });
    }

    refresh() {
      const t = this.t;
      const live = this.items.filter((i) => !i.removed);
      let posters = 0; let frames = 0; let mats = 0;
      const total = { price: 0, compare: 0 };
      live.forEach((item) => {
        const lines = this.lines(item);
        lines.forEach((l) => { if (l.kind === 'poster') posters++; if (l.kind === 'frame') frames++; if (l.kind === 'mat') mats++; });
        const s = this.sums(lines);
        total.price += s.price; total.compare += s.compare;
      });

      if (this.els.counts) {
        const parts = [`${esc(t.posters)}: ${posters}`, `${esc(t.frames)}: ${frames}`];
        if (mats) parts.push(`${esc(t.mats)}: ${mats}`);
        this.els.counts.innerHTML = parts.map((p) => `<span>${p}</span>`).join('<span class="art-wall__dot" aria-hidden="true">·</span>');
      }
      if (this.els.total) {
        this.els.total.textContent = this.money(total.price);
        this.els.total.classList.toggle('is-sale', total.compare > total.price);
      }
      if (this.els.compare) {
        const sale = total.compare > total.price;
        this.els.compare.hidden = !sale;
        this.els.compare.textContent = sale ? this.money(total.compare) : '';
      }
      if (this.els.save) {
        const sale = total.compare > total.price;
        this.els.save.hidden = !sale;
        this.els.save.textContent = sale ? `-${Math.round((1 - total.price / total.compare) * 100)}%` : '';
      }
      if (this.els.cta && !this.busy) {
        this.els.cta.disabled = live.length === 0;
        this.els.ctaLabel.textContent = live.length === 0 ? t.ctaEmpty : t.cta;
      }
      this.renderSummary();
      this.renderPlan();
    }

    // Outer size in cm of an item as it will hang (frame size with passe-partout), oriented.
    outerCm(item) {
      const landscape = (item.p.ratio || 0.7) > 1.02;
      const d = dims(item.mat !== 'none' ? this.frameSizeN(item) : this.variant(item).title) || [50, 70];
      return landscape ? [Math.max(d[0], d[1]), Math.min(d[0], d[1])] : [Math.min(d[0], d[1]), Math.max(d[0], d[1])];
    }

    // Signature: a to-scale hanging plan of the wall that reacts live.
    renderPlan() {
      const { plan } = this.els;
      if (!plan) return;
      const live = this.items.filter((i) => !i.removed);
      if (!live.length) { plan.innerHTML = ''; return; }
      const sizes = live.map((i) => this.outerCm(i));
      const maxH = Math.max(...sizes.map((d) => d[1]));
      const tallest = window.matchMedia('(max-width: 749px)').matches ? 36 : 46;
      const scale = tallest / maxH; // px per cm, same scale for every piece
      plan.innerHTML = live.map((item, n) => {
        const [w, h] = sizes[n];
        return `<span class="art-wall__plan-piece" data-frame="${esc(item.frame)}" data-mat="${esc(item.mat)}" style="width:${(w * scale).toFixed(1)}px;height:${(h * scale).toFixed(1)}px"><img src="${esc(item.p.thumb)}" alt=""></span>`;
      }).join('');
    }

    /* ---------- popover ---------- */

    isMobile() { return window.matchMedia('(max-width: 749px)').matches; }

    openSheet(item, kind, btn) {
      const { sheet, options, sheetTitle } = this.els;
      if (!sheet) return;
      this.closeSheet(false);
      this.sheetCtx = { item, kind, btn };
      const opts = this.options(item, kind);
      sheetTitle.textContent = `${{ size: this.t.size, frame: this.t.frame, mat: this.t.mat }[kind]} · ${item.p.title}`;
      options.innerHTML = opts.map((o, n) => `
        <li class="aw-opt${o.selected ? ' is-selected' : ''}${o.disabled ? ' is-disabled' : ''}" role="option" id="aw-opt-${n}"
          aria-selected="${o.selected}"${o.disabled ? ' aria-disabled="true"' : ''} data-value="${esc(o.value)}">
          ${o.swatch ? `<span class="aw-opt__swatch" data-swatch="${esc(o.swatch)}" aria-hidden="true"></span>` : ''}
          <span class="aw-opt__text">
            <span class="aw-opt__label">${esc(o.label)}</span>
            ${o.sub || o.note ? `<span class="aw-opt__sub">${esc(o.note || o.sub)}</span>` : ''}
          </span>
          ${o.price != null ? this.priceHtml(o.price, o.compare) : ''}
          <span class="aw-opt__check">${o.selected ? CHECK : ''}</span>
        </li>`).join('');
      // Bring a half-hidden trigger into view first (desktop dropdown anchors to it).
      if (!this.isMobile()) {
        const r = btn.getBoundingClientRect();
        const bar = this.root.querySelector('[data-aw-bar]');
        const barTop = bar ? bar.getBoundingClientRect().top : window.innerHeight;
        if (r.top < (parseFloat(this.stickyTop) || 0) + 60 || r.bottom > Math.min(window.innerHeight, barTop) - 8) {
          btn.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }
      sheet.hidden = false;
      sheet.classList.toggle('is-sheet', this.isMobile());
      btn.setAttribute('aria-expanded', 'true');
      options.setAttribute('aria-labelledby', btn.id);
      this.position();
      void sheet.offsetWidth; // commit start state so the transition runs
      sheet.classList.add('is-open');
      const sel = options.querySelector('.is-selected') || options.querySelector('.aw-opt:not(.is-disabled)');
      this.focusOpt(sel);
      options.focus({ preventScroll: true });
    }

    position() {
      const { sheet } = this.els;
      const panel = sheet.querySelector('.art-wall__sheet-panel');
      if (!this.sheetCtx) return;
      if (sheet.classList.contains('is-sheet')) { panel.style.cssText = ''; return; }
      const r = this.sheetCtx.btn.getBoundingClientRect();
      const width = Math.max(r.width, 300);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      panel.style.width = `${width}px`;
      panel.style.left = `${left}px`;
      panel.style.top = '';
      panel.style.bottom = '';
      panel.style.maxHeight = '';
      const natural = Math.min(panel.scrollHeight, 420);
      const topLimit = (parseFloat(this.stickyTop) || 0) + 8;
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - topLimit - 4;
      if (natural <= below || below >= above) {
        panel.style.maxHeight = `${Math.max(160, Math.min(natural, below))}px`;
        panel.style.top = `${r.bottom + 4}px`;
      } else {
        const h = Math.min(natural, above);
        panel.style.maxHeight = `${h}px`;
        panel.style.top = `${r.top - h - 4}px`;
      }
    }

    focusOpt(li) {
      const { options } = this.els;
      options.querySelectorAll('.aw-opt.is-focus').forEach((x) => x.classList.remove('is-focus'));
      if (!li) return;
      li.classList.add('is-focus');
      options.setAttribute('aria-activedescendant', li.id);
      li.scrollIntoView({ block: 'nearest' });
    }

    closeSheet(returnFocus = true) {
      const { sheet } = this.els;
      if (!sheet || sheet.hidden) return;
      const ctx = this.sheetCtx;
      sheet.classList.remove('is-open');
      sheet.hidden = true;
      this.sheetCtx = null;
      if (ctx && ctx.btn) {
        ctx.btn.setAttribute('aria-expanded', 'false');
        if (returnFocus) {
          const again = ctx.item.el && ctx.item.el.querySelector(`[data-aw-open="${ctx.kind}"]`);
          (again || ctx.btn).focus({ preventScroll: true });
        }
      }
    }

    choose(li) {
      if (!li || li.classList.contains('is-disabled') || !this.sheetCtx) return;
      const { item, kind } = this.sheetCtx;
      const value = li.dataset.value;
      this.closeSheet(false);
      this.select(item, kind, value);
      const again = item.el.querySelector(`[data-aw-open="${kind}"]`);
      if (again) again.focus({ preventScroll: true });
    }

    /* ---------- events ---------- */

    bind() {
      const { root } = this;

      root.addEventListener('click', (e) => {
        const open = e.target.closest('[data-aw-open]');
        if (open && !open.disabled) {
          const item = this.itemFrom(open);
          if (item) {
            if (this.sheetCtx && this.sheetCtx.btn === open) this.closeSheet();
            else this.openSheet(item, open.dataset.awOpen, open);
          }
          return;
        }
        const rm = e.target.closest('[data-aw-remove]');
        if (rm) {
          const item = this.itemFrom(rm);
          if (!item) return;
          const avail = this.variant(item).available || item.variants.some((v) => v.available);
          if (item.removed && !avail) return;
          if (item.removed && !this.variant(item).available) {
            const v = item.variants.find((x) => x.available);
            if (v) item.variantId = v.id;
          }
          item.removed = !item.removed;
          this.updateCard(item);
          this.refresh();
          const inSummary = rm.closest('[data-aw-summary]');
          const again = (inSummary ? this.els.summary.querySelector(`[data-index="${this.items.indexOf(item)}"] [data-aw-remove]`) : item.el.querySelector('[data-aw-remove]'));
          if (again) again.focus({ preventScroll: true });
          return;
        }
        const reset = e.target.closest('[data-aw-reset]');
        if (reset) {
          const item = this.itemFrom(reset);
          if (!item) return;
          Object.assign(item, item.def);
          this.validate(item);
          this.updateCard(item);
          this.refresh();
          const rmBtn = item.el.querySelector('[data-aw-remove]');
          if (rmBtn) rmBtn.focus({ preventScroll: true });
          return;
        }
        if (e.target.closest('[data-aw-sheet-close]')) { this.closeSheet(); return; }
        const opt = e.target.closest('.aw-opt');
        if (opt) { this.choose(opt); return; }
        if (e.target.closest('[data-aw-zoom]') || e.target.closest('[data-aw-gallery] img')) { this.openLightbox(); return; }
        if (e.target.closest('[data-aw-lightbox]')) { this.closeLightbox(); return; }
        const tab = e.target.closest('[data-aw-tab]');
        if (tab) { this.showTab(tab.dataset.awTab); return; }
        const add = e.target.closest('[data-aw-add]');
        if (add) { const item = this.itemFrom(add); if (item) this.addOne(item); return; }
        if (e.target.closest('[data-aw-cta]')) { this.addAll(); }
      });

      // Tabs keyboard
      const tabs = Array.from(root.querySelectorAll('[data-aw-tab]'));
      tabs.forEach((tab, n) => tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = tabs[(n + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        next.focus();
        this.showTab(next.dataset.awTab);
      }));

      // Listbox keyboard
      this.els.options && this.els.options.addEventListener('keydown', (e) => {
        const all = Array.from(this.els.options.querySelectorAll('.aw-opt:not(.is-disabled)'));
        const cur = this.els.options.querySelector('.aw-opt.is-focus');
        let idx = all.indexOf(cur);
        if (e.key === 'ArrowDown') { e.preventDefault(); this.focusOpt(all[Math.min(all.length - 1, idx + 1)]); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.focusOpt(all[Math.max(0, idx - 1)]); }
        else if (e.key === 'Home') { e.preventDefault(); this.focusOpt(all[0]); }
        else if (e.key === 'End') { e.preventDefault(); this.focusOpt(all[all.length - 1]); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.choose(cur); }
        else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); this.closeSheet(); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeSheet(); this.closeLightbox(); } });
      document.addEventListener('click', (e) => {
        if (!this.sheetCtx) return;
        if (e.target.closest('.art-wall__sheet-panel') || e.target.closest('[data-aw-open]')) return;
        this.closeSheet(false);
      }, true);
      window.addEventListener('scroll', () => { if (this.sheetCtx && !this.els.sheet.classList.contains('is-sheet')) this.position(); }, { passive: true });
      window.addEventListener('resize', () => this.closeSheet(false));

      // Carousel pager (mobile)
      const track = this.els.track;
      if (track) {
        let raf = 0;
        track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => this.updatePager());
        }, { passive: true });
        track.addEventListener('scrollend', () => this.updatePager(), { passive: true });
        track.addEventListener('keydown', (e) => {
          if (e.target !== track) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            this.goTo(this.active + (e.key === 'ArrowRight' ? 1 : -1));
          }
        });
      }
      if (this.els.pagerDots) {
        this.els.pagerDots.addEventListener('click', (e) => {
          const dot = e.target.closest('span');
          if (!dot) return;
          this.goTo(Array.from(this.els.pagerDots.children).indexOf(dot));
        });
      }
      this.updatePager();

      // Hero gallery dots
      const g = this.els.gallery;
      if (g && this.els.galleryDots) {
        g.addEventListener('scroll', () => {
          const n = Math.round(g.scrollLeft / Math.max(1, g.clientWidth));
          Array.from(this.els.galleryDots.children).forEach((d, i) => d.classList.toggle('is-active', i === n));
        }, { passive: true });
      }

      // Keep sticky tabs/aside below whatever the theme pins at the top (announcement bar + header).
      const pinned = (el) => {
        for (let n = el, k = 0; n && n !== document.body && k < 6; n = n.parentElement, k++) {
          const p = getComputedStyle(n).position;
          if (p === 'fixed' || p === 'sticky') return true;
        }
        return false;
      };
      const syncTop = () => {
        const els = Array.from(document.querySelectorAll('.shopify-section-group-header-group, .shopify-section-group-header-group > *, header'))
          .filter((el) => !this.root.contains(el));
        let bottom = 0;
        // Chain pinned bars from the top of the viewport down (announcement bar, then header, ...).
        for (let pass = 0; pass < 3; pass++) {
          els.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (!r.height || r.bottom <= bottom || r.top > bottom + 2 || r.bottom > window.innerHeight / 2) return;
            const cs = getComputedStyle(el);
            if (cs.visibility === 'hidden' || cs.display === 'none' || Number(cs.opacity) === 0) return;
            if (pinned(el)) bottom = r.bottom;
          });
        }
        const v = `${Math.max(0, Math.floor(bottom) - 1)}px`;
        if (v !== this.stickyTop) { this.stickyTop = v; this.root.style.setProperty('--aw-sticky-top', v); }
      };
      let stRaf = 0;
      const onScrollTop = () => { cancelAnimationFrame(stRaf); stRaf = requestAnimationFrame(syncTop); };
      window.addEventListener('scroll', onScrollTop, { passive: true });
      window.addEventListener('resize', onScrollTop);
      // The theme animates its header in/out; re-measure once the transition settles.
      new MutationObserver(() => { onScrollTop(); setTimeout(onScrollTop, 350); }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      syncTop();

      // Frame compositor may load after us.
      document.addEventListener('peleton:frames-ready', () => this.items.forEach((it) => this.updateCard(it)));
    }

    openLightbox() {
      const box = this.root.querySelector('[data-aw-lightbox]');
      const g = this.els.gallery;
      if (!box || !g) return;
      const n = Math.round(g.scrollLeft / Math.max(1, g.clientWidth));
      const img = g.querySelectorAll('img')[n] || g.querySelector('img');
      if (!img) return;
      const big = box.querySelector('[data-aw-lightbox-img]');
      big.src = img.currentSrc || img.src;
      if (img.srcset) { big.srcset = img.srcset; big.sizes = '100vw'; }
      box.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      this.lastFocus = document.activeElement;
      box.querySelector('[data-aw-lightbox-close]').focus({ preventScroll: true });
    }

    closeLightbox() {
      const box = this.root.querySelector('[data-aw-lightbox]');
      if (!box || box.hidden) return;
      box.hidden = true;
      document.documentElement.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus({ preventScroll: true });
    }

    itemFrom(el) {
      const host = el.closest('[data-index]');
      return host ? this.items[Number(host.dataset.index)] : null;
    }

    showTab(name) {
      const { root } = this;
      root.querySelectorAll('[data-aw-tab]').forEach((tab) => {
        const on = tab.dataset.awTab === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on);
        tab.tabIndex = on ? 0 : -1;
      });
      root.querySelectorAll('[data-aw-panel]').forEach((p) => { p.hidden = p.dataset.awPanel !== name; });
      this.closeSheet(false);
      if (name === 'customize') this.updatePager();
      // If the tabs are pinned, jump back to the start of the panel so the new tab starts at the top.
      const tabs = root.querySelector('[data-aw-tabs]');
      const stick = parseFloat(this.stickyTop) || 0;
      const included = root.querySelector('[data-aw-included]');
      const target = window.scrollY + included.getBoundingClientRect().top - tabs.offsetHeight - stick - 8;
      if (tabs.getBoundingClientRect().top <= stick + 2 && window.scrollY > target) {
        window.scrollTo({ top: Math.max(0, target), behavior: 'auto' });
      }
    }

    goTo(n) {
      const track = this.els.track;
      const cards = track.children;
      if (!cards.length) return;
      const i = Math.max(0, Math.min(cards.length - 1, n));
      const card = cards[i];
      track.scrollTo({ left: card.offsetLeft - (parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0), behavior: 'smooth' });
    }

    updatePager() {
      const track = this.els.track;
      if (!track || !track.children.length) return;
      const cards = Array.from(track.children);
      const mid = track.scrollLeft + 1;
      let n = 0;
      const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      cards.forEach((c, i) => { if (c.offsetLeft - pad - c.offsetWidth / 2 <= mid) n = i; });
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) n = cards.length - 1;
      this.active = n;
      if (this.els.pagerCount) this.els.pagerCount.textContent = `${n + 1} ${this.t.of || '/'} ${cards.length}`;
      if (this.els.pagerDots) Array.from(this.els.pagerDots.children).forEach((d, i) => d.classList.toggle('is-active', i === n));
    }

    /* ---------- cart ---------- */

    // Cart lines for the given wall items: poster, frame, passe-partout, all tagged with the wall,
    // frame/passe-partout linked to the poster SKU (the warehouse flow reads _Poster to frame).
    cartItems(list) {
      const wall = this.data.wall;
      const base = {};
      if (this.data.cartProperty) base[this.data.cartProperty] = wall.title;
      base['_Art wall'] = wall.handle;
      const items = [];
      list.forEach((item) => {
        const lines = this.lines(item);
        const poster = lines[0].v;
        const ref = poster.sku || `${item.p.handle} ${poster.title}`;
        lines.forEach((l) => {
          const props = { ...base };
          if (l.kind === 'frame') props['_Poster to frame'] = ref;
          if (l.kind === 'mat') props['_Passepartout for'] = ref;
          items.push({ id: l.v.id, quantity: 1, properties: props });
        });
      });
      return items;
    }

    // One /cart/add.js call, then open the theme's cart drawer (cart.js listens for ajaxProduct:added).
    async postToCart(items) {
      const res = await fetch(this.data.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        // Shopify lists the newest line first; send in reverse so the cart reads poster, frame, passe-partout, in wall order.
        body: JSON.stringify({ items: items.slice().reverse() })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.description || json.message || this.t.error);
      return json;
    }

    openDrawer(json) {
      const drawer = document.querySelector('drawer-cart cart-items, [data-drawer] cart-items');
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { items: json.items || [], source: 'art-wall' } }));
      if (!drawer) window.location.href = this.data.cartUrl;
    }

    // Per-card add: this poster in its chosen size + its frame + passe-partout. The wall total is untouched.
    async addOne(item) {
      if (!item || item.removed || item.addState === 'loading') return;
      clearTimeout(item.addTimer);
      item.addState = 'loading';
      item.addError = '';
      this.updateCard(item);
      try {
        const json = await this.postToCart(this.cartItems([item]));
        item.addState = 'done';
        this.updateCard(item);
        this.openDrawer(json);
        item.addTimer = setTimeout(() => { item.addState = ''; this.updateCard(item); }, 2200);
      } catch (err) {
        item.addState = '';
        item.addError = (err && err.message) || this.t.error;
        this.updateCard(item);
      }
    }

    async addAll() {
      if (this.busy) return;
      const live = this.items.filter((i) => !i.removed);
      if (!live.length) return;
      const items = this.cartItems(live);

      this.busy = true;
      const { cta, msg } = this.els;
      cta.disabled = true;
      cta.classList.add('is-loading');
      msg.textContent = '';
      try {
        const json = await this.postToCart(items);
        cta.classList.remove('is-loading');
        cta.classList.add('is-done');
        this.openDrawer(json);
      } catch (err) {
        cta.classList.remove('is-loading');
        msg.textContent = (err && err.message) || this.t.error;
      } finally {
        setTimeout(() => {
          this.busy = false;
          cta.classList.remove('is-done');
          this.refresh();
        }, 2200);
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-art-wall]').forEach((root) => {
      if (root.__artWall) return;
      root.__artWall = new ArtWall(root);
    });
  };

  window.PeletonArtWall = { init, ArtWall };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
